/**
 * useLadekostenzuschuss — CRUD-Hook fuer Ladekostenzuschuss (Issue #200).
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { Ladekostenzuschuss } from '@/lib/database.types';
import {
  findeUeberlappung,
  validiereLadekostenzuschussEingabe,
  type LadekostenzuschussEingabe,
} from '@/lib/ladekostenzuschuss';

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return msg.includes('PGRST205') || msg.includes('schema cache') || msg.includes('relation') || msg.includes('does not exist');
}

export interface MutationsErgebnis {
  ok: boolean;
  error?: string;
}

export function useLadekostenzuschuss() {
  const [eintraege, setEintraege] = useState<Ladekostenzuschuss[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEintraege = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ladekostenzuschuss')
        .select('*')
        .order('gueltig_ab', { ascending: false });
      if (error) {
        if (isTableNotFound(error)) {
          setEintraege([]);
        } else {
          throw error;
        }
      } else {
        setEintraege((data || []) as Ladekostenzuschuss[]);
      }
    } catch {
      setEintraege([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEintraege(); }, [fetchEintraege]);

  const erstelleEintrag = useCallback(async (eingabe: LadekostenzuschussEingabe): Promise<MutationsErgebnis> => {
    const validation = validiereLadekostenzuschussEingabe(eingabe);
    if (!validation.valid) return { ok: false, error: validation.error };

    const ueberlappung = findeUeberlappung(eingabe, eintraege);
    if (ueberlappung) {
      return {
        ok: false,
        error: `Ueberlappender Eintrag existiert (${ueberlappung.gueltig_ab} – ${ueberlappung.gueltig_bis ?? 'unbefristet'}).`,
      };
    }

    try {
      const { error } = await supabase.from('ladekostenzuschuss').insert(eingabe);
      if (error) throw error;
      await fetchEintraege();
      return { ok: true };
    } catch (err: any) {
      const msg = err?.message || 'Speichern fehlgeschlagen';
      return { ok: false, error: msg };
    }
  }, [eintraege, fetchEintraege]);

  const aktualisiereEintrag = useCallback(async (
    id: string,
    eingabe: LadekostenzuschussEingabe,
  ): Promise<MutationsErgebnis> => {
    const validation = validiereLadekostenzuschussEingabe(eingabe);
    if (!validation.valid) return { ok: false, error: validation.error };

    const ueberlappung = findeUeberlappung({ id, ...eingabe }, eintraege);
    if (ueberlappung) {
      return {
        ok: false,
        error: `Ueberlappender Eintrag existiert (${ueberlappung.gueltig_ab} – ${ueberlappung.gueltig_bis ?? 'unbefristet'}).`,
      };
    }

    try {
      const { error } = await supabase.from('ladekostenzuschuss').update(eingabe).eq('id', id);
      if (error) throw error;
      await fetchEintraege();
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Speichern fehlgeschlagen' };
    }
  }, [eintraege, fetchEintraege]);

  const deaktiviereEintrag = useCallback(async (id: string, stichtag?: string): Promise<MutationsErgebnis> => {
    const tag = stichtag || new Date().toISOString().slice(0, 10);
    try {
      const { error } = await supabase
        .from('ladekostenzuschuss')
        .update({ gueltig_bis: tag })
        .eq('id', id);
      if (error) throw error;
      await fetchEintraege();
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Deaktivierung fehlgeschlagen' };
    }
  }, [fetchEintraege]);

  const loescheEintrag = useCallback(async (id: string): Promise<MutationsErgebnis> => {
    try {
      const { error } = await supabase.from('ladekostenzuschuss').delete().eq('id', id);
      if (error) throw error;
      await fetchEintraege();
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message || 'Loeschen fehlgeschlagen' };
    }
  }, [fetchEintraege]);

  return {
    eintraege,
    loading,
    erstelleEintrag,
    aktualisiereEintrag,
    deaktiviereEintrag,
    loescheEintrag,
    refetch: fetchEintraege,
  };
}
